/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  Grid,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  TextField,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Skeleton,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, useAppDispatch } from "@store/store";
import { useLazyGetLoanUseCategoriesConfigQuery, useLazyGetBusinessSectorQuery } from "@api/catalogs";
import {
  useLazyGetLoanCreditUseQuery,
  useLazyPostLoanUseQuery,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { ProgressBar } from "@components/ProgressBar";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { setLoanStatus } from "@store/slices/registerSlice";
import { ApiError, LoanCreditUseStructure } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import icon_check from "@assets/img/icon_check_cotizador.svg";
import { ButtonTypeSelected } from "@components/ButtonTypeSelected";
import useStatusConfig from "@hooks/useStatusConfig";
import { ItemLabelValue } from "@interfaces/catalogs";
import useTofuSteps from "@hooks/useTofuSteps";

const SCREEN_STATUS = "CSUDC";

const UsoDecreditoAvanzaSolido = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);

  const applicationData = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getLoanCreditUse] = useLazyGetLoanCreditUseQuery();
  const [triggerSaveUseSelected] = useLazyPostLoanUseQuery();
  const [getLoanUseCategoriesConfig] = useLazyGetLoanUseCategoriesConfigQuery();
  const [getBusinessSector] = useLazyGetBusinessSectorQuery();

  const [bussinesSectorList, setBussinesSectorList] = useState<ItemLabelValue[]>([]);
  const [creditUseList, setCreditUseList] = useState<ItemLabelValue[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      business_sector_id: "",
      reason: "",
      activity: "",
      uses_identifiers: [] as string[],
    },
    validationSchema: yup.object().shape({
      business_sector_id: yup.string().required("Campo requerido"),
      reason: yup.string().required("Campo requerido"),
      activity: yup.string().required("Campo requerido"),
      uses_identifiers: yup.array().of(yup.string()).required("Campo requerido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!formik.isValid) {
          return;
        }

        if (!nextStatus) {
          throw new Error("No se encontró el siguiente estado");
        }

        setSubmitting(true);

        const uses_identifiers = values.uses_identifiers.map((id) => ({
          resources_use_id: id,
        }));

        await triggerSaveUseSelected({
          loanId: applicationData.id,
          body: {
            reason: values.reason,
            activity: values.activity,
            business_sector_id: values.business_sector_id,
            resource_uses_identifiers: uses_identifiers,
          },
        }).unwrap();

        await triggerPostNextStage({
          applicationId: applicationData.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(setLoanStatus(nextStatus.code));
        navigate(nextStatus.url);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "UsoDeCredito",
            values: values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const getCurrentLoanCreditUse = async (): Promise<LoanCreditUseStructure | null> => {
    try {
      return await getLoanCreditUse(applicationData.id).unwrap();
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [business_sector, list_credit_use, response_loan_credit_use] = await Promise.all([
          getBusinessSector().unwrap(),
          getLoanUseCategoriesConfig({ taxCode: applicationData.type }).unwrap(),
          getCurrentLoanCreditUse(),
        ]);

        const list_bussines_sector = business_sector.map((item) => ({
          label: item.name,
          value: item.id,
          is_other: false,
          order: item.order,
        }));
        // sort list_bussines_sector by order
        list_bussines_sector.sort((a, b) => a.order - b.order);

        const list_credit_use_mapped = list_credit_use.map((item) => ({
          value: item.loan_use_categories_id,
          label: item.loan_use_categories.name,
          is_other: item.loan_use_categories.other,
          order: item.loan_use_categories.order,
        }));
        // sort list_credit_use_mapped by order
        list_credit_use_mapped.sort((a, b) => a.order - b.order);

        setBussinesSectorList(list_bussines_sector);
        setCreditUseList(list_credit_use_mapped);

        if (response_loan_credit_use && response_loan_credit_use.id) {
          formik.setValues({
            business_sector_id: response_loan_credit_use.business_sector_id,
            reason: response_loan_credit_use.reason,
            activity: response_loan_credit_use.activity,
            uses_identifiers: response_loan_credit_use.resource_uses_identifiers.map(
              (item) => item.resources_use_id,
            ) as string[],
          });

          setTimeout(() => {
            formik.validateForm();
          }, 200);
        }
      } catch (error) {
        console.log("Ha ocurrido un error al obtener los datos", error);
        Sentry.captureException(error, {
          extra: {
            component: "UsoDeCredito",
            values: {},
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={4}>
        <Box
          sx={{
            mt: isMobileOrTablet ? 2 : 4,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            Destino de los recursos
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
            mt={5}
          >
            Sector destino
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Box display="flex" flexDirection="row" gap={2} justifyContent="center">
                {!isLoading ? (
                  bussinesSectorList.map((item) => (
                    <ButtonTypeSelected
                      key={item.value}
                      title={item.label}
                      onClick={() => {
                        formik.setFieldValue("business_sector_id", item.value);
                      }}
                      isSelected={formik.values.business_sector_id === item.value}
                      isMovile={isMobileOrTablet}
                      height="45px"
                      heightMoviles="40px"
                      width="105px"
                      widthMoviles="70px"
                      icon={icon_check}
                      width_icon="24px"
                      height_icon="24px"
                    />
                  ))
                ) : (
                  <Box mt={1} width="100%">
                    <Skeleton variant="text" animation={"wave"} height="45px" />
                  </Box>
                )}
              </Box>

              <Typography
                variant={isMobileOrTablet ? "h4" : "h3"}
                fontWeight={500}
                textAlign="center"
                color={theme?.palette?.primary?.dark}
                mt={5}
              >
                Uso de los recursos
              </Typography>

              {!isLoading ? (
                creditUseList.map((item: ItemLabelValue, index: number) => {
                  return (
                    <Box mt={1} key={index}>
                      <FormControlLabel
                        sx={{ color: theme?.palette?.primary?.dark }}
                        control={
                          <Checkbox
                            key={item.value}
                            id={item.value}
                            name="uses"
                            value={item.value}
                            checked={formik.values.uses_identifiers.includes(item.value)}
                            disabled={formik.isSubmitting}
                            onChange={(e) => {
                              const { value, checked } = e.target;
                              const new_value = checked
                                ? [...formik.values.uses_identifiers, value]
                                : formik.values.uses_identifiers.filter((id) => id !== value);

                              formik.setFieldValue("uses_identifiers", new_value);
                            }}
                          />
                        }
                        label={item.label}
                      />
                    </Box>
                  );
                })
              ) : (
                <Box mt={1} width="100%">
                  <Skeleton variant="text" animation={"wave"} height="45px" />
                </Box>
              )}

              <TextField
                id="reason"
                name="reason"
                label="Describe el destino del crédito"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.reason}
                error={formik.touched.reason && Boolean(formik.errors.reason)}
                helperText={formik.touched.reason && formik.errors.reason}
                disabled={isLoading || formik.isSubmitting}
                inputProps={{ maxLength: 150 }}
                multiline
                rows={4}
              />

              <Typography
                variant={isMobileOrTablet ? "h4" : "h3"}
                fontWeight={500}
                textAlign="center"
                color={theme?.palette?.primary?.dark}
                mt={2}
                mb={2}
              >
                Actividad económica
              </Typography>

              <TextField
                id="activity"
                name="activity"
                label="Describe la actividad de la empresa"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.activity}
                error={formik.touched.activity && Boolean(formik.errors.activity)}
                helperText={formik.touched.activity && formik.errors.activity}
                disabled={isLoading || formik.isSubmitting}
                inputProps={{ maxLength: 150 }}
                multiline
                rows={4}
              />

              <Box display={"flex"} justifyContent={"center"} mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button type="submit" variant="default" disabled={!formik.isValid || formik.isSubmitting}>
                  Continuar
                </Button>
              </Box>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default UsoDecreditoAvanzaSolido;
